import { useCallback, useState } from 'react';

interface Toaster {
  success: (message: string) => void;
  error: (message: string) => void;
}

export type RunAction = <T = any>(key: string, fn: () => Promise<T>, success?: string) => Promise<T | true | undefined>;

/** What child cards receive from a page's useAction(). */
export interface ActionProps {
  run: RunAction;
  busy: string | null;
}

/**
 * Runs one async action at a time and remembers which one is busy.
 * run('finalize', () => api.post(...), 'Invoice finalized') shows a toast and returns the result (or undefined on error).
 */
export function useAction(toast: Toaster) {
  const [busy, setBusy] = useState<string | null>(null);
  const run = useCallback(
    async <T = any>(key: string, fn: () => Promise<T>, success?: string): Promise<T | true | undefined> => {
      setBusy(key);
      try {
        const result = await fn();
        if (success) toast.success(success);
        return result ?? true;
      } catch (err) {
        toast.error((err as Error).message);
        return undefined;
      } finally {
        setBusy(null);
      }
    },
    [toast],
  );
  return { busy, run };
}
